import { Link } from "react-router-dom";
import Seo, { ORG_JSONLD } from "@/components/Seo";
import { Section, Container, Btn, ArrowLink, SectionHead, Stat } from "@/components/kit";
import { Reveal, Eyebrow } from "@/lib/anim";
import { STATS_HOME, SERVICE_LINKS } from "@/lib/content";
import { IMG, SERVICE_IMG } from "@/lib/images";

const VALUES = [
  ["Save time", "Skip months of trial and error. Prox brings a proven buying and sourcing playbook straight to your product."],
  ["Reduce risk", "Avoid the costly first-order mistakes: the wrong factory, the wrong quantities, and margins that cannot survive a discount."],
  ["Make more money", "Better sourcing and sharper pricing put points back on your gross margin, where they compound season after season."],
  ["Work with people you trust", "Buyer-side only. No commission on quotes, no markup, and every supplier contact stays yours."],
];

export default function Home() {
  return (
    <>
      <Seo canonical="/" jsonLd={ORG_JSONLD} />

      {/* Hero */}
      <section className="pt-14 md:pt-20 pb-16 md:pb-24" style={{ backgroundColor: "var(--bone)" }}>
        <Container>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7">
              <Eyebrow>Product sourcing, procurement, pricing and go-to-market</Eyebrow>
              <h1 className="t-display1" data-testid="hero-heading">Source better. Price smarter. Launch with confidence.</h1>
              <p className="t-lead mt-7 measure text-[var(--muted)]">
                Prox helps founders and growing consumer brands source the right products, build profitable product strategies, negotiate with suppliers, and launch with confidence.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <Btn to="/services" variant="clay" data-testid="hero-services">Explore what we do</Btn>
                <Btn to="/book" variant="outline">Book a call<span className="arrow-anim">→</span></Btn>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] overflow-hidden" style={{ borderRadius: 2 }}>
                <img src={IMG.hero} alt="Warm-toned fabric, folded" className="img-cover duo" loading="eager" />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Trust strip */}
      <Section bg="charcoal" dark pad={false} className="py-14 md:py-16">
        <p className="eyebrow mb-8">Built on thirteen years of buying and sourcing</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
          {STATS_HOME.map((s, i) => <Reveal i={i} key={s.c}><Stat n={s.n} c={s.c} dark testid={`stat-${i}`} /></Reveal>)}
        </div>
      </Section>

      {/* What we do */}
      <Section bg="bone">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead eyebrow="What we do" title="From sourcing and procurement to pricing and launch." lead="Six defined pieces of work. Pick the one that fits, or start with a free call." />
          <ArrowLink to="/services">All services</ArrowLink>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {SERVICE_LINKS.map((s, i) => (
            <Reveal i={i % 3} key={s.slug}>
              <Link to={`/services/${s.slug}`} className="card block h-full overflow-hidden" data-testid={`service-card-${s.slug}`}>
                <div className="aspect-[16/10] overflow-hidden"><img src={SERVICE_IMG[s.slug]} alt="" className="img-cover duo" loading="lazy" /></div>
                <div className="p-6">
                  <h3 className="t-h4">{s.title}</h3>
                  <span className="lnk mt-4 inline-flex">Read more<span className="arrow-anim">→</span></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Who we help */}
      <Section bg="sand">
        <SectionHead eyebrow="Who we help" title="Two starting points." />
        <div className="grid md:grid-cols-2 gap-5 mt-10">
          {[
            { h: "Launching a new product", p: "You have an idea and a budget, and you want the first run sourced, costed and sized properly.", to: "/services/launch-plan-for-new-brands", label: "See the Launch Plan" },
            { h: "Scaling an existing brand", p: "Revenue is growing but margin is flat. The fix is usually cost, range and buying discipline.", to: "/services/costing-pricing-and-margin", label: "See the Margin work" },
          ].map((a, i) => (
            <Reveal i={i} key={a.h}>
              <Link to={a.to} className="card block h-full p-8 md:p-10" data-testid={`audience-${i}`}>
                <h3 className="t-h3">{a.h}</h3>
                <p className="t-body mt-4 text-[var(--muted)]">{a.p}</p>
                <span className="lnk mt-6 inline-flex">{a.label}<span className="arrow-anim">→</span></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Why Prox */}
      <Section bg="bone">
        <SectionHead eyebrow="Why Prox" title="You are buying confidence, not just expertise." />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {VALUES.map((v, i) => (
            <Reveal i={i} key={v[0]}>
              <div>
                <div className="w-8 h-1 bg-[var(--clay)] mb-5" />
                <h3 className="t-h4">{v[0]}</h3>
                <p className="t-sm text-[var(--muted)] mt-3">{v[1]}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* How it works */}
      <Section bg="sand">
        <SectionHead eyebrow="How it works" title="A simple, low-risk way to start." lead="No long proposals and no follow-up sequence. Four clear steps, and you can stop after any of them." />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
          {[
            ["Book a free call", "Twenty minutes to understand your product, your numbers and where the real risk sits."],
            ["Get a fixed-price plan", "A written scope and a fixed price. Nothing starts until you approve it."],
            ["Prox does the work", "Focused sessions on your real costs, suppliers and buy, with models and notes you keep."],
            ["Launch with confidence", "A clear plan you can act on, and every supplier contact stays yours."],
          ].map((s, i) => (
            <Reveal i={i} key={s[0]}>
              <div>
                <div className="font-mono text-[var(--clay-deep)] text-sm">0{i + 1}</div>
                <h3 className="t-h4 mt-3">{s[0]}</h3>
                <p className="t-sm text-[var(--muted)] mt-3">{s[1]}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Results strip + case */}
      <Section bg="charcoalDeep" dark>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead eyebrow="Results" title="Numbers moved, on real product." dark />
          <ArrowLink to="/case-studies" dark>See the case studies</ArrowLink>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10 mt-12 pb-12 border-b border-white/12">
          {[
            ["-36%", "Taken out of product cost on a $60M category"],
            ["+8pp", "Gross margin lift in 18 months"],
            ["300+", "Products launched for a brand from zero"],
            ["6", "Sourcing countries, negotiated direct"],
          ].map((r, i) => (
            <Reveal i={i} key={r[0]}>
              <div>
                <div className="t-stat text-[var(--bone)]" style={{ fontSize: "clamp(30px,3.4vw,46px)" }}>{r[0]}</div>
                <p className="t-sm text-[var(--muted-dark)] mt-3">{r[1]}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="t-caption text-[var(--muted-dark)] mt-6 measure">Delivered across thirteen years on the buying side. These results were achieved as an employee of the businesses described, not as Prox client work. See the case studies for the full context.</p>
      </Section>

      {/* Closing CTA */}
      <Section bg="clay" dark className="py-20 md:py-28">
        <div className="max-w-3xl">
          <Eyebrow>Start here</Eyebrow>
          <h2 className="t-display2">Book a free 20-minute call.</h2>
          <p className="t-lead mt-6 measure" style={{ color: "rgba(255,255,255,0.85)" }}>A straight answer on how Prox can help, which piece of work fits, and what it costs. No deck, no follow-up sequence.</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Btn to="/book" variant="bone">Book the call</Btn>
            <Btn to="/pricing" variant="outline-bone">See pricing</Btn>
          </div>
        </div>
      </Section>
    </>
  );
}
